import * as THREE from "three";

/**
 * 64×64 textures painted in code and quantised to a small palette — the kind of texture a 1997
 * artist would have hand-pixelled. No external assets needed to prove the look; Midjourney crops
 * can replace these later via the same quantise() step.
 */
const SIZE = 128; // power of two so mipmaps work; detail scales with `u` below

function canvas(draw: (g: CanvasRenderingContext2D, s: number, u: number) => void, levels = 8): THREE.CanvasTexture {
  const c = document.createElement("canvas"); c.width = SIZE; c.height = SIZE;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  draw(g, SIZE, SIZE / 64);
  quantize(g, levels);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Posterise each channel to `levels` steps — a crude stand-in for an indexed palette. */
export function quantize(g: CanvasRenderingContext2D, levels: number): void {
  const img = g.getImageData(0, 0, g.canvas.width, g.canvas.height);
  const step = 255 / (levels - 1);
  for (let i = 0; i < img.data.length; i += 4) for (let k = 0; k < 3; k++) img.data[i + k] = Math.round(img.data[i + k]! / step) * step;
  g.putImageData(img, 0, 0);
}

const rnd = (seed: number) => () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };

export const textures = {
  /** Deck grating: dark steel bars over a void, with oil, wear and the odd dropped washer. */
  grating: () => canvas((g, s, u) => {
    const r = rnd(1);
    g.fillStyle = "#141815"; g.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 8 * u) {
      g.fillStyle = "#3d443e"; g.fillRect(0, y, s, 2.5 * u);
      g.fillStyle = "#585f57"; g.fillRect(0, y, s, 1 * u);            // lit top edge of each bar
    }
    for (let x = 0; x < s; x += 16 * u) { g.fillStyle = "#2b312c"; g.fillRect(x, 0, 2 * u, s); }
    g.fillStyle = "rgba(0,0,0,0.45)";
    for (let i = 0; i < 10; i++) { const x = r() * s, y = r() * s; g.beginPath(); g.ellipse(x, y, (4 + r() * 9) * u, (3 + r() * 6) * u, r() * 3, 0, 7); g.fill(); }
    g.fillStyle = "rgba(150,160,150,0.18)";
    for (let i = 0; i < 14; i++) g.fillRect(r() * s, r() * s, 2 * u, 1 * u);   // scuffs
  }),
  /** Quilted wall padding: diamond tufts, seams, and grime pooling at the bottom. */
  padding: () => canvas((g, s, u) => {
    const r = rnd(4);
    g.fillStyle = "#6d6a5c"; g.fillRect(0, 0, s, s);
    g.strokeStyle = "#3d3b33"; g.lineWidth = 2 * u;
    for (let i = -s; i < s * 2; i += 16 * u) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + s, s); g.stroke(); g.beginPath(); g.moveTo(i, s); g.lineTo(i + s, 0); g.stroke(); }
    g.strokeStyle = "rgba(255,250,230,0.10)"; g.lineWidth = 1 * u;
    for (let i = -s; i < s * 2; i += 16 * u) { g.beginPath(); g.moveTo(i - 1, 1); g.lineTo(i + s - 1, s + 1); g.stroke(); }
    for (let y = 8 * u; y < s; y += 16 * u) for (let x = 8 * u; x < s; x += 16 * u) {
      g.fillStyle = "#8a8677"; g.beginPath(); g.ellipse(x, y, 3 * u, 3 * u, 0, 0, 7); g.fill();
      g.fillStyle = "#4a4740"; g.fillRect(x - 0.5 * u, y - 0.5 * u, 1.2 * u, 1.2 * u);   // the button
    }
    const grime = g.createLinearGradient(0, s * 0.55, 0, s);
    grime.addColorStop(0, "rgba(20,18,12,0)"); grime.addColorStop(1, "rgba(18,16,10,0.5)");
    g.fillStyle = grime; g.fillRect(0, s * 0.55, s, s * 0.45);
    g.fillStyle = "rgba(30,25,15,0.25)";
    for (let i = 0; i < 8; i++) { const x = r() * s; g.fillRect(x, r() * s * 0.7, 2 * u, s); }  // drips
  }),
  /** Steel panel: recessed centre, rivets, a weld seam and a scuff. */
  steel: () => canvas((g, s, u) => {
    const r = rnd(9);
    g.fillStyle = "#4a5550"; g.fillRect(0, 0, s, s);
    g.fillStyle = "#3a433f"; g.fillRect(0, 0, s, 3 * u); g.fillRect(0, 0, 3 * u, s);
    g.fillStyle = "#5d6a64"; g.fillRect(s - 3 * u, 0, 3 * u, s); g.fillRect(0, s - 3 * u, s, 3 * u);
    g.strokeStyle = "#39423e"; g.lineWidth = 1.5 * u; g.strokeRect(9 * u, 9 * u, s - 18 * u, s - 18 * u);
    g.strokeStyle = "rgba(150,165,158,0.35)"; g.lineWidth = 1 * u; g.strokeRect(10 * u, 10 * u, s - 20 * u, s - 20 * u);
    for (const [x, y] of [[6, 6], [s / u - 8, 6], [6, s / u - 8], [s / u - 8, s / u - 8], [s / u / 2 - 1, 6], [s / u / 2 - 1, s / u - 8]]) {
      g.fillStyle = "#222826"; g.fillRect(x! * u, y! * u, 3 * u, 3 * u);
      g.fillStyle = "#6b7772"; g.fillRect(x! * u, y! * u, 1.5 * u, 1.5 * u);
    }
    g.fillStyle = "rgba(0,0,0,0.22)"; g.fillRect(20 * u, 30 * u, 28 * u, 3 * u);
    g.fillStyle = "rgba(160,150,130,0.10)";
    for (let i = 0; i < 10; i++) g.fillRect(r() * s, r() * s, (2 + r() * 6) * u, 1 * u);
  }),
  /** Hazard stripe, yellow/black diagonal, chipped and dirty. */
  hazard: () => canvas((g, s, u) => {
    const r = rnd(3);
    g.fillStyle = "#1a1a12"; g.fillRect(0, 0, s, s);
    g.fillStyle = "#c9a227";
    for (let i = -s; i < s * 2; i += 16 * u) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 8 * u, 0); g.lineTo(i + 8 * u + s, s); g.lineTo(i + s, s); g.closePath(); g.fill(); }
    g.fillStyle = "rgba(255,240,190,0.16)";
    for (let i = -s; i < s * 2; i += 16 * u) g.fillRect(i, 0, 1.5 * u, s);
    g.fillStyle = "rgba(30,30,20,0.55)";
    for (let i = 0; i < 22; i++) g.fillRect(r() * s, r() * s, (3 + r() * 7) * u, (1 + r() * 2) * u);   // chips
  }, 6),
  /** Console face: recessed lamp rows, a label strip and finger grime. */
  console: () => canvas((g, s, u) => {
    const r = rnd(7);
    g.fillStyle = "#2f3a35"; g.fillRect(0, 0, s, s);
    const lamps = ["#e0483a", "#e8b44a", "#5fd166", "#48bcd8"];
    for (let y = 6 * u; y < s; y += 10 * u) {
      g.fillStyle = "#171d1a"; g.fillRect(3 * u, y, s - 6 * u, 6 * u);
      g.fillStyle = "rgba(255,255,255,0.05)"; g.fillRect(3 * u, y, s - 6 * u, 1 * u);
      for (let x = 7 * u; x < s - 6 * u; x += 6 * u) {
        if (r() < 0.34) {
          const c = lamps[Math.floor(r() * lamps.length)]!;
          g.fillStyle = c; g.fillRect(x, y + 1.5 * u, 3 * u, 3 * u);
          g.fillStyle = "rgba(255,255,255,0.5)"; g.fillRect(x, y + 1.5 * u, 1 * u, 1 * u);
        } else { g.fillStyle = "#101413"; g.fillRect(x, y + 1.5 * u, 3 * u, 3 * u); }
      }
    }
    g.fillStyle = "rgba(0,0,0,0.18)";
    for (let i = 0; i < 6; i++) { g.beginPath(); g.ellipse(r() * s, r() * s, 6 * u, 4 * u, 0, 0, 7); g.fill(); }
  }),
  /** Ceiling: ribbed ducting with a specular top on each rib. */
  duct: () => canvas((g, s, u) => {
    g.fillStyle = "#2a2e2c"; g.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 6 * u) {
      g.fillStyle = y % (12 * u) < 0.5 ? "#3c4240" : "#1f2322"; g.fillRect(0, y, s, 3 * u);
      g.fillStyle = "rgba(190,205,200,0.09)"; g.fillRect(0, y, s, 1 * u);
    }
    g.fillStyle = "rgba(0,0,0,0.3)"; g.fillRect(0, s * 0.72, s, 2 * u);
  }),
  /** Label plate: engraved lettering groove on brushed alloy. */
  plate: () => canvas((g, s, u) => {
    const r = rnd(11);
    g.fillStyle = "#b9b4a0"; g.fillRect(0, 0, s, s);
    g.strokeStyle = "rgba(255,255,245,0.18)"; g.lineWidth = 1;
    for (let i = 0; i < 40; i++) { const y = r() * s; g.beginPath(); g.moveTo(0, y); g.lineTo(s, y); g.stroke(); }
    g.fillStyle = "#2b2a24"; g.fillRect(6 * u, s / 2 - 2 * u, s - 12 * u, 4 * u);
    g.fillStyle = "rgba(255,255,250,0.35)"; g.fillRect(6 * u, s / 2 + 2 * u, s - 12 * u, 1 * u);
    g.fillStyle = "#8d887a"; g.fillRect(0, 0, s, 2 * u); g.fillRect(0, s - 2 * u, s, 2 * u);
  }),
  /** Screen glass where the DOM terminal does not cover it. */
  phosphor: () => canvas((g, s, u) => {
    g.fillStyle = "#0a1a10"; g.fillRect(0, 0, s, s);
    g.fillStyle = "#123322"; for (let y = 0; y < s; y += 2 * u) g.fillRect(0, y, s, 1 * u);
  }),
};
